/**
 * MH-14 Hàng đợi duyệt (mục 5.4 đặc tả giao diện) — phần màn hình duyệt.
 *
 * Bắt buộc: nút DUYỆT chỉ bật sau khi người dùng đã nghe hết bản tin (sự kiện
 * `onNgheHet` của `TrinhPhatAmThanh`) HOẶC chủ động bấm "Xác nhận đã nghe". Máy
 * chủ CŨNG chặn lại (mã lỗi `OPERATION_NOT_ALLOWED` nếu gọi `duyet` mà chưa gọi
 * `danhDauDaNghe`) — giao diện disable nút để không cho bấm nhầm, còn máy chủ
 * là hàng rào thật sự, đúng nguyên tắc "giao diện không phải nơi bảo đảm an
 * toàn duy nhất".
 *
 * Ba hành động: duyệt, yêu cầu sửa (ý kiến bắt buộc), từ chối (lý do bắt buộc).
 *
 * Gộp kho (29/10/2026): bản tin hình ảnh/video không có đoạn âm thanh — màn hình hiện tệp xem
 * trước qua `GET /bulletins/:id/file-url` và nút "Xác nhận đã xem" (cùng `listened`).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viBienTapNoiDung } from '../ngon-ngu/vi-bien-tap';
import {
  banTinApi,
  nhomLoaiBanTin,
  type DongBanTin,
  type DongDoanAmThanh,
  type DongDuyetLog,
} from '../api/ban-tin-api';
import { kieuTep, TrinhXemTep } from './BangTinChung';
import './bang-tin.css';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi } from '../thanh-phan/TrangThaiManHinh';
import { TrinhPhatAmThanh } from '../thanh-phan/TrinhPhatAmThanh';

export interface MH14DetailProps {
  api?: Pick<typeof banTinApi, 'chiTiet' | 'doanAmThanh' | 'nhatKyDuyet' | 'danhDauDaNghe' | 'duyet'> &
    Partial<Pick<typeof banTinApi, 'linkTep'>>;
}

type HanhDongDuyet = 'duyet' | 'tra_lai' | 'tu_choi';

export function MH14DuyetBanTin({ api }: MH14DetailProps = {}): ReactNode {
  const goi = api ?? banTinApi;
  const { id } = useParams<{ id: string }>();
  const dieuHuong = useNavigate();
  const [daNghe, datDaNghe] = useState(false);
  const [dangGuiDaNghe, datDangGuiDaNghe] = useState(false);
  const [hanhDong, datHanhDong] = useState<HanhDongDuyet | null>(null);
  const [yKien, datYKien] = useState('');
  const [dangGui, datDangGui] = useState(false);
  const [loiHanhDong, datLoiHanhDong] = useState<string | null>(null);

  const banTin = dungTaiDuLieu<DongBanTin>({
    tai: (signal) => goi.chiTiet(id as string, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [id],
    tat: !id,
  });

  const goiLinkTep = api?.linkTep ?? banTinApi.linkTep;
  const laAnhVideo = banTin.duLieu ? nhomLoaiBanTin(banTin.duLieu.loaiBanTin) !== 'am-thanh' : false;
  const [urlTep, datUrlTep] = useState<string | null | 'loi'>(null);
  useEffect(() => {
    if (!id || !laAnhVideo) return undefined;
    const boHuy = new AbortController();
    goiLinkTep(id, boHuy.signal)
      .then((kq) => {
        if (!boHuy.signal.aborted) datUrlTep(kq.url || 'loi');
      })
      .catch(() => {
        if (!boHuy.signal.aborted) datUrlTep('loi');
      });
    return () => boHuy.abort();
  }, [id, laAnhVideo, goiLinkTep]);

  const doan = dungTaiDuLieu<DongDoanAmThanh[]>({
    tai: (signal) => goi.doanAmThanh.danhSach(id as string, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [id],
    tat: !id,
  });

  const nhatKy = dungTaiDuLieu<DongDuyetLog[]>({
    tai: (signal) => goi.nhatKyDuyet(id as string, signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [id],
    tat: !id,
  });

  const xacNhanDaNghe = async (): Promise<void> => {
    if (!id) return;
    datDangGuiDaNghe(true);
    try {
      await goi.danhDauDaNghe(id);
      datDaNghe(true);
    } finally {
      datDangGuiDaNghe(false);
    }
  };

  const canYKien = hanhDong === 'tra_lai' || hanhDong === 'tu_choi';

  const duyetNgay = async (): Promise<void> => {
    if (!id) return;
    datDangGui(true);
    datLoiHanhDong(null);
    try {
      await goi.duyet(id, 'duyet');
      dieuHuong('/ban-tin/duyet');
    } catch (e: unknown) {
      datLoiHanhDong(e instanceof Error ? e.message : 'Không ghi nhận được quyết định');
    } finally {
      datDangGui(false);
    }
  };

  const guiQuyetDinh = async (): Promise<void> => {
    if (!id || !hanhDong) return;
    if (canYKien && !yKien.trim()) {
      datLoiHanhDong(hanhDong === 'tra_lai' ? vi.duyet.yKienBatBuoc : vi.duyet.lyDoBatBuoc);
      return;
    }
    datDangGui(true);
    datLoiHanhDong(null);
    try {
      await goi.duyet(id, hanhDong, yKien.trim() || undefined);
      dieuHuong('/ban-tin/duyet');
    } catch (e: unknown) {
      datLoiHanhDong(e instanceof Error ? e.message : 'Không ghi nhận được quyết định');
    } finally {
      datDangGui(false);
    }
  };

  if (banTin.trangThai === 'dang-tai') return <KhungXuong soDong={8} soCot={2} taiLau={banTin.taiLau} />;
  if (banTin.trangThai === 'loi' && banTin.loi)
    return <TrangThaiLoi loi={banTin.loi} onThuLai={banTin.taiLai} />;
  if (!banTin.duLieu) return null;

  const dong = banTin.duLieu;
  const doanDauTien = (doan.duLieu ?? [])[0];

  return (
    <div className="man-hinh" data-man-hinh="MH-14-duyet">
      <h1>{dong.tieuDe}</h1>
      <p>{dong.noiDungTomTat}</p>
      <p data-vung="pham-vi">{dong.tenDiaBanTao ?? dong.diaBanTao}</p>

      {laAnhVideo ? (
        <section
          className="bt-xem-truoc"
          data-vung="xem-tep-duyet"
          aria-label={viBienTapNoiDung.hangDoi.xemTep}
        >
          <h2>{viBienTapNoiDung.hangDoi.xemTep}</h2>
          {urlTep === 'loi' ? <p role="alert">{viBienTapNoiDung.hangDoi.khongCoTep}</p> : null}
          {urlTep && urlTep !== 'loi' ? (
            <TrinhXemTep
              url={urlTep}
              kieu={kieuTep(dong.loaiBanTin, urlTep, dong.tep?.mime)}
              nhan={dong.tieuDe}
            />
          ) : null}
        </section>
      ) : (
        <TrinhPhatAmThanh
          nhan={dienVao('Nghe {ten}', { ten: dong.tieuDe })}
          url={doanDauTien?.urlNgheThu ?? undefined}
          onNgheHet={() => {
            if (!daNghe) void xacNhanDaNghe();
          }}
        />
      )}

      <p>{vi.duyet.dieuKienDuyet}</p>
      {!daNghe ? (
        <button type="button" className="nut" onClick={() => void xacNhanDaNghe()} disabled={dangGuiDaNghe}>
          {laAnhVideo ? viBienTapNoiDung.hangDoi.xacNhanDaXem : vi.duyet.xacNhanDaNghe}
        </button>
      ) : (
        <p data-vung="da-nghe-xac-nhan">{vi.duyet.daNghe}</p>
      )}

      <h2>{vi.duyet.nhatKyDuyet}</h2>
      {nhatKy.trangThai === 'dang-tai' ? <KhungXuong soDong={3} soCot={1} /> : null}
      {nhatKy.trangThai === 'co-du-lieu' ? (
        <ul data-vung="nhat-ky-duyet">
          {(nhatKy.duLieu ?? []).map((n) => (
            <li key={n.id}>
              {n.thoiDiem} — {n.trangThaiTruoc ?? '—'} → {n.trangThaiSau} {n.yKien ? `(${n.yKien})` : ''}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="man-hinh__hanh-dong" data-vung="hanh-dong-duyet">
        <button
          type="button"
          className="nut nut--chinh"
          data-hanh-dong="duyet"
          disabled={!daNghe || dangGui}
          onClick={() => void duyetNgay()}
        >
          {vi.duyet.coDuyet}
        </button>
        <button type="button" className="nut" onClick={() => datHanhDong('tra_lai')}>
          {vi.duyet.yeuCauSua}
        </button>
        <button type="button" className="nut" onClick={() => datHanhDong('tu_choi')}>
          {vi.duyet.tuChoi}
        </button>
      </div>

      {canYKien ? (
        <div data-vung="ho-so-y-kien">
          <label htmlFor="duyet-y-kien">{vi.duyet.nhanYKien}</label>
          <textarea id="duyet-y-kien" value={yKien} onChange={(e) => datYKien(e.target.value)} />
          {loiHanhDong ? (
            <p role="alert" data-vung="loi-hanh-dong">
              {loiHanhDong}
            </p>
          ) : null}
          <button
            type="button"
            className="nut nut--chinh"
            onClick={() => void guiQuyetDinh()}
            disabled={dangGui}
          >
            {hanhDong === 'tra_lai' ? vi.duyet.yeuCauSua : vi.duyet.tuChoi}
          </button>
          <button type="button" className="nut" onClick={() => datHanhDong(null)}>
            {vi.xacNhan.huy}
          </button>
        </div>
      ) : null}

      <button type="button" className="nut nut--lien-ket" onClick={() => dieuHuong('/ban-tin/duyet')}>
        {vi.duyet.quayLaiHangDoi}
      </button>
    </div>
  );
}
